import { getToken } from "../storage/authStorage";
import { API_BASE_URL } from "../../../config/api";

/**
 * Which actions wait for a reviewer before they post.
 *
 * The rules live in `review_policies` (role × module × action × requires_review)
 * and they are load-bearing: `PermissionService::requiresReview` gates real
 * behaviour across invoices, bills and credit notes through `PermissionsContext`,
 * and the tenant already holds well over a thousand `review_requests`.
 *
 * SAVING GOES THROUGH `POST /api/roles/update`, NOT `POST /api/review-policies/{id}`.
 * The per-row endpoint can only update a policy that already exists, and most
 * granted actions have no row at all — so it can never turn review ON for an
 * action that has never had it. `roles/update` does a full replace of a role's
 * permissions and review policies inside a DB transaction, which both creates
 * missing rows and makes a multi-toggle save atomic.
 *
 * THE PRICE: that endpoint DELETES every permission row for the role and
 * reinserts from the payload. An incomplete `permissions` map silently strips
 * the role's permissions. So `buildRoleUpdatePayload` echoes the granted set
 * back verbatim and is the only place allowed to construct that call.
 */

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

/** One action a module supports. */
export interface ActionRef {
  code: string;
  name: string;
}

/** A module and every action defined against it. */
export interface ModuleRef {
  code: string;
  name: string;
  actions: ActionRef[];
}

/** What a role may do and what waits for review, as flat `module.action` keys. */
export interface RoleRules {
  granted: Set<string>;
  review: Set<string>;
}

const request = async <T>(
  path: string,
  method: "GET" | "POST",
  body?: unknown,
): Promise<T> => {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/** Flat key for a module-action pair. The same shape the backend stores in `key`. */
export const ruleKey = (moduleCode: string, actionCode: string): string =>
  `${moduleCode}.${actionCode}`;

/**
 * Every module and the actions defined against it — 18 modules, 90 pairs.
 *
 * Needed because a role's own payload lists only what it HAS. Rendering an
 * action the role cannot perform (master calls it "Not allowed") requires the
 * full catalogue to subtract from.
 */
export const fetchModulesAndActions = async (): Promise<ModuleRef[]> => {
  const raw = await request<{ data: any[] }>("/api/roles/modules-actions", "POST", {});

  return (raw.data ?? []).map(
    (row): ModuleRef => ({
      code: String(row.module_code ?? ""),
      name: String(row.module_name ?? ""),
      actions: (row.actions ?? []).map(
        (a: any): ActionRef => ({
          code: String(a.action_code ?? ""),
          name: String(a.action_name ?? ""),
        }),
      ),
    }),
  );
};

/**
 * What one role may do, and which of those wait for review.
 *
 * `permissions` arrives as a list of modules each carrying an action map; every
 * stored row is a grant, so presence is the grant. `review_policies` arrives as
 * `{module_code: {action_code: true}}` and only lists the ones set.
 */
export const fetchRoleRules = async (roleId: number): Promise<RoleRules> => {
  const raw = await request<{
    data: {
      permissions?: {
        module_code?: string;
        actions?: Record<string, { is_granted?: boolean }>;
      }[];
      review_policies?: Record<string, Record<string, boolean>>;
    };
  }>(`/api/roles/get?role_id=${roleId}`, "GET");

  const granted = new Set<string>();
  (raw.data?.permissions ?? []).forEach((module) => {
    const moduleCode = String(module.module_code ?? "");
    Object.entries(module.actions ?? {}).forEach(([actionCode, action]) => {
      if (action?.is_granted) granted.add(ruleKey(moduleCode, actionCode));
    });
  });

  const review = new Set<string>();
  Object.entries(raw.data?.review_policies ?? {}).forEach(([moduleCode, actions]) => {
    Object.entries(actions ?? {}).forEach(([actionCode, requiresReview]) => {
      if (requiresReview) review.add(ruleKey(moduleCode, actionCode));
    });
  });

  return { granted, review };
};

/**
 * The payload for `POST /api/roles/update`, built from the full rule sets.
 *
 * `role_name` and `is_manager_required` are deliberately ABSENT. The endpoint
 * only touches the `roles` row for keys that are present, and it also uses
 * `is_manager_required` to decide whether to write review policies at all —
 * sending either by accident would rename the role or silently discard every
 * rule on the page.
 */
export const buildRoleUpdatePayload = (
  roleId: number,
  modules: ModuleRef[],
  rules: RoleRules,
): {
  role_id: number;
  permissions: Record<string, Record<string, boolean>>;
  review_policies: Record<string, Record<string, boolean>>;
} => {
  const permissions: Record<string, Record<string, boolean>> = {};
  const review_policies: Record<string, Record<string, boolean>> = {};

  modules.forEach((module) => {
    module.actions.forEach((action) => {
      const key = ruleKey(module.code, action.code);
      if (rules.granted.has(key)) {
        permissions[module.code] ??= {};
        permissions[module.code][action.code] = true;
      }
      if (rules.review.has(key)) {
        review_policies[module.code] ??= {};
        review_policies[module.code][action.code] = true;
      }
    });
  });

  return { role_id: roleId, permissions, review_policies };
};

/**
 * Write a role's review rules.
 *
 * Replaces the role's permissions and review policies wholesale, in one
 * transaction, so a page full of toggles either all lands or none of it does.
 */
export const saveRoleRules = async (payload: {
  role_id: number;
  permissions: Record<string, Record<string, boolean>>;
  review_policies: Record<string, Record<string, boolean>>;
}): Promise<{ success: boolean; message: string }> =>
  request<{ success: boolean; message: string }>("/api/roles/update", "POST", payload);
